import {
  Component,
  inject,
  input,
  Input,
  model,
  OnChanges,
  output,
  Renderer2,
  signal,
  SimpleChanges,
  viewChild,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import { ListingColInterface } from "../../../baseListing/interfaces/listing-config.interface";
import { Table, TableColumnReorderEvent, TableModule } from "primeng/table";
import { TableSelectEventInterface, TableRowReorderEventInterface } from "../../interfaces/table-events.interface";
import { TableConfigInterface } from "../../interfaces/table-config.interface";
import { OverlayPanelComponent } from "../../../../shared/modules/overlay-panel/components/overlay-panel/overlay-panel.component";
import { DynamicChildLoaderDirective } from "../../directives/dynamic-component.directive";
import { BaseListing } from "../../../baseListing/classes/base-listing";
import { RouterLink } from "@angular/router";
import { TableUserOptionsInterface } from "../../interfaces/table-user-options.interface";
import { LocalStorageService } from "../../../../tools/services/local-storage.service";
import { AuthService } from "../../../../tools/services/auth.service";
import { GraphService } from "../../../../graphql/services/graph.service";
import { NgTemplateOutlet } from "@angular/common";
import { TableColumnToggleComponent } from "../table-column-toggle/table-column-toggle.component";
import { Skeleton } from "primeng/skeleton";
import { TableActionComponent } from "../table-action/table-action.component";
import { TranslatePipe } from "@ngx-translate/core";
import { accessDeep, cloneDeep } from "../../../../shared/helpers";
import { SvgIconComponent } from "../../../../tools/svg-icon/components/svg-icon/svg-icon.component";
import { ListActionInterface } from "@app/kernel/listing/interfaces/listing-items-actions.interface";
import { OverlayMenuListComponent } from "@kernel/shared/modules/overlay-panel/components/overlay-menu-list/overlay-menu-list.component";
import { OverlayMenuItemComponent } from "@kernel/shared/modules/overlay-panel/components/overlay-menu-item/overlay-menu-item.component";

@Component({
  selector: "sxw-table",
  templateUrl: "./table.component.html",
  styleUrls: ["./table.component.scss"],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgTemplateOutlet,
    TranslatePipe,
    TableModule,
    TableColumnToggleComponent,
    Skeleton,
    RouterLink,
    TableActionComponent,
    OverlayPanelComponent,
    DynamicChildLoaderDirective,
    SvgIconComponent,
    OverlayMenuListComponent,
    OverlayMenuItemComponent,
  ],
})
export class TableComponent extends BaseListing implements OnChanges {
  declare config: TableConfigInterface;
  overlay = viewChild<OverlayPanelComponent>("overlay");
  dynamicChild = viewChild(DynamicChildLoaderDirective);
  primeTable = viewChild<Table>("table");

  scrollable = input(true);
  scrollHeight = input("flex");
  noDataPage = input("");
  reorderableRows = input(false);

  checkboxDt = input<Record<string, unknown>>();

  @Input() set sort(value: { field: string; order: 1 | -1 }) {
    this._sort = value;
  }
  // onSelect = new EventEmitter<TableSelectEventInterface>();
  onSelect = output<TableSelectEventInterface>();
  onRowReorder = output<TableRowReorderEventInterface>();

  // The "_cols" variable stores the columns from "config.cols" after they've been ordered
  _cols = signal<ListingColInterface[]>([]);

  userOptions = signal<TableUserOptionsInterface>({ order: [], cols: {} });
  selectedSet = new Set<any>();
  rowIdentifierKey = input("id");
  selectAll = model<boolean | null>(null);
  hideAllRowsSelection = input<boolean>(false);
  hiddenActions = new Map<ListActionInterface, Set<ListActionInterface>>();

  // Drag and drop state
  draggedRowIndex: number | null = null;
  dragOverRowIndex: number | null = null;

  private localStorage = inject(LocalStorageService);
  private renderer = inject(Renderer2);
  constructor(graphService: GraphService, authService: AuthService) {
    super(graphService, authService);
  }

  override ngOnChanges(changes: SimpleChanges) {
    if (changes["config"]) {
      this.loadUserOptions();

      this.prepareConfig();
    }
    /**
     * primeng expect to find selection array of objects to be like _data exactly (all properties are equal each other)
     * but the issue here _data has more properties like __class, ...etc
     * so when selectedData come from outside should has these properties to be like _data
     * primeng compare between them like that:
     * {id: 1, name: 'test'} === {id: 1, name: 'test'} => true
     * {id: 1, name: 'test'} === {id: 1, name: 'test2'} => false
     * {id: 1, name: 'test'} === {id: 1} => false
     */
    if (changes["selectedData"]) {
      this.selectedData.update(data =>
        data.map(d => ({
          ...d,
          __class: this.getRowClass(d),
          __formatted__: this.format(d),
          __background: this.getRowBackground(d),
        }))
      );
      this.resetSelectedRows();
    }
    super.ngOnChanges(changes);
  }

  //******************************************************************************//
  //******************************* Public Methods *******************************//
  //******************************************************************************//

  /**
   * Sets the component loading status
   * @param state
   */
  loading(state = true) {
    this._loading = state;
  }

  /**
   * Force re-render the table.
   */
  reRender(): void {
    this.processChanges();
  }

  /**
   * Force re-load the table data.
   */
  reload(loading = true): void {
    if (!this.config.query) {
      return;
    }
    this.executeQuery(loading);
    this._errored = false;
  }

  /**
   * Sets the table data.
   * @param data
   */
  setDate(data: any): void {
    this.data = data;
    this.processDataChanges();
  }

  /**
   * Removes a row
   * @param index
   */
  removeItem(index: number) {
    this.data.splice(index, 1);
    this.addItemClass(index, "deleted-row");
    setTimeout(() => this.addItemClass(index, "d-none"), 400);
  }

  /**
   * adds class to item.
   * @param index
   * @param _class
   */
  addItemClass(index: number, _class: string) {
    if (!this._data || !this._data[index]) {
      return;
    }
    (this._data[index] as any).__class[_class] = true;
  }

  /**
   * Returns the current sort status
   * @returns sort
   */
  getSortStatus(): any {
    return this._sort.order && this._sort.field ? this._sort : undefined;
  }

  /**
   * removes class from item
   * @param index
   * @param _class
   */
  removeItemClass(index: number, _class: string) {
    if (!this._data || !this._data[index]) {
      return;
    }
    delete (this._data[index] as any).__class[_class];
  }
  /**
   * Sets item value
   * @param index
   * @param key
   * @param value
   */
  setItemValue(index: number, key: string, value: any) {
    const _key = key.split(".");
    let _item = this.data[index] ?? {};
    for (let i = 0; i < _key.length - 1; ++i) {
      _item = _item[_key[i]] ?? {};
    }
    _item[_key[_key.length - 1]] = value;
    this.processDataChanges();
  }

  handleActionVisibilityChange(action: ListActionInterface, parent: ListActionInterface, isVisible: boolean | null) {
    const subActionsSet = this.hiddenActions.get(parent) || new Set();
    if (isVisible) {
      subActionsSet.delete(action);
    } else {
      subActionsSet.add(action);
    }
    this.hiddenActions.set(parent, subActionsSet);
  }

  //*******************************************************************************//
  //******************************** Table Methods ********************************//
  //*******************************************************************************//

  /**
   * Prepares Data only to be used in the view
   * @protected
   */
  protected processDataChanges() {
    this.prepareConfig();
    this._data = [];
    for (let index = 0; index < this.data?.length; ++index) {
      this._data.push({
        ...this.data[index],
        __class: this.getRowClass(this.data[index]),
        __formatted__: this.format(this.data[index]),
        __background: this.getRowBackground(this.data[index]),
      });
    }
  }

  /**
   * Handles a query response by parsing the data and updating the component's state.
   * This function is typically used in the context of handling Graphql query responses.
   * @param data - The response data, which can be in a nested structure.
   */
  handleQueryResponse(data: any) {
    this.data = cloneDeep(data?.data?.[this.config.query as string]?.data ?? []);
    this.changeTotalResults(data?.data?.[this.config.query as string]?.pagination?.total ?? 0);
    setTimeout(() => {
      // lazy process DATA to prioritize the UI

      this.processDataChanges();
      this.loading(false);
    });
  }

  /**
   * Returns formatted data row
   * @param row
   */
  protected format(row: any): any {
    const __row: any = {};
    this.config.cols?.forEach((col: ListingColInterface) => {
      __row[<string>col.id] = this.getCellValue(row, col);
    });
    return __row;
  }

  /**
   * Extracts the value of a cell
   * @param row
   * @param col
   */
  protected getCellValue(row: any, col: ListingColInterface): string {
    if (typeof col.format === "function") {
      return col.format(row) ?? "";
    }
    if (Array.isArray(col.field)) {
      return col.field.map(key => this.extractCellValue(row, key)).join(",");
    }
    return this.extractCellValue(row, <string>col.field);
  }

  /**
   * Extracts the value from a row using a key, made to handle nested objects
   * ex: row = {address: {street: '#VALUE'}}, key = 'address.street'
   * @param row
   * @param key
   * @protected
   */
  protected extractCellValue(row: any, key: string): string {
    return key ? `${accessDeep(row, key) ?? ""}` : "";
  }
  /**
   * Changes the status of actions list to active/not-active
   * the actions list status is used to keep the 3 dots visible when the user is hovering the menu and not the row.
   * @param index
   * @param status
   */
  toggleActionsList(index: number, status: boolean) {
    if (!this.data[index]) {
      return;
    }
    this.data[index].__actionsListActive = status;
  }

  /**
   * Prepares the config by adding the userOptions to it.
   * TODO: something is not correct in this method, the _cols set then updated directly, it needs to be refactored
   */
  prepareConfig() {
    if (this.userOptions()?.order && this.userOptions().order.length === this.config.cols.length) {
      // Sort the columns on a different array to keep track of the original order
      this._cols.set(
        (this.userOptions()?.order?.map(id => this.config.cols.find(col => col.id === id)) as ListingColInterface[]) ?? this.config.cols
      );
    }

    this._cols.update(cols => {
      return cols.map(col => ({
        ...col,
        width: this.userOptions().cols?.[col.id!]?.width ?? col.width,
        hidden: this.userOptions().cols[col.id!]?.hidden ?? col.hidden,
      }));
    });
    this.config.width = this.userOptions().tableWidth ?? this.config.width;
  }

  /**
   * Updates the user options.
   * @param options
   */
  updateUserOptions(options: any): TableComponent {
    // this.userOptions.set(Object.assign(this.userOptions, options));
    this.userOptions.update(userOptions => ({
      ...userOptions,
      ...options,
    }));
    return this;
  }

  /**
   * Saves the current user options to the suitable storage.
   */
  flushUserOptions(): TableComponent {
    this.localStorage.set(this.config.id!, this.userOptions());
    return this;
  }

  /**
   * Loads the user options from the suitable storage.
   */
  loadUserOptions() {
    this.userOptions.update(userOptions => ({
      ...userOptions,
      order: this.config.cols.map(col => col.id!),
      ...(this.localStorage.get(this.config.id!) ?? {}),
    }));
  }

  getRowIdentifier(row: any) {
    if (this.rowIdentifierKey() && this.rowIdentifierKey() in row) {
      return row[this.rowIdentifierKey()];
    }
    return row;
  }

  isSelectedRow(row: any) {
    return this.selectedSet.has(this.getRowIdentifier(row));
  }

  resetSelectedRows() {
    this.selectedSet.clear();
    this.selectedData().forEach((item: any) => this.selectedSet.add(this.getRowIdentifier(item)));
  }
  //******************************************************************************//
  //******************************* Events Methods *******************************//
  //******************************************************************************//

  /**
   * Triggers select event.
   * @param _event
   * @param type
   */
  triggerSelect(_event: any, type: "selected" | "removed" = "selected") {
    this.resetSelectedRows();
    this.onSelect.emit({ items: this.selectedData(), [type]: _event.data });
  }

  /**
   * Changes the dragged column order
   * @param event
   */
  // reorderCol(event: { dragIndex: number, dropIndex: number, columns: ListingColInterface[] }) {
  reorderCol(event: TableColumnReorderEvent) {
    const order = event.columns?.map(col => col.id!);
    // update the current user options, then save the updates to the suitable storage,
    this.updateUserOptions({ order }).flushUserOptions();
    this._editTableStyles();
  }

  /**
   * Changes the resized column width
   * @param event
   */
  resizeCol(event: { element: HTMLElement; delta: any }) {
    const cols = this.userOptions().cols;
    const colId = event.element.id?.split("-")[1] ?? this.config.cols[0].id!;
    cols[colId] = { ...cols[colId], width: `${event.element.clientWidth}` }; // set the col new width;
    // update the current user options, then save the updates to the suitable storage
    this.updateUserOptions({
      cols,
      tableWidth: `${event.element.closest("table")?.style.width}`, // set the table new width
    }).flushUserOptions(); // save the user options updates
  }

  /**
   * Handle drag start event
   * @param event
   * @param index
   * @param row
   */
  onDragStart(event: DragEvent, index: number, row: any) {
    this.draggedRowIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", index.toString());
    }
    if (row) {
      row.__dragging = true;
    }
  }

  /**
   * Handle drag end event
   * @param _event
   * @param row
   */
  onDragEnd(_event: DragEvent, row: any) {
    this.draggedRowIndex = null;
    this.draggedOverRow.set(null);
    if (row) {
      row.__dragging = false;
    }
  }

  private draggedOverRow = signal<any | null>(null);

  /**
   * Handle drag over event
   * @param event
   * @param row
   */
  onDragOver(event: DragEvent, row: any) {
    event.preventDefault();

    const existingDragOverItem = this.draggedOverRow();
    if (existingDragOverItem) {
      existingDragOverItem.__dragOver = false;
    }

    this.draggedOverRow.set(row);

    // Only set drag over state if row is defined
    if (row) {
      row.__dragOver = true;
    }

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  /**
   * Handle drop event
   * @param event
   * @param dropIndex
   */
  onDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    this.draggedOverRow.set(null);
    if (this.draggedRowIndex === null || this.draggedRowIndex === dropIndex) {
      this.draggedRowIndex = null;
      return;
    }

    // Get the data before moving it
    const draggedData = this.data[this.draggedRowIndex];

    // Calculate the new order based on the drop position
    const newOrder = Math.max(Math.min(dropIndex + 1, this.data.length), 1);

    // Update local data
    if (this.data && this.data.length > Math.max(this.draggedRowIndex, dropIndex)) {
      const movedItem = this.data.splice(this.draggedRowIndex, 1)[0];
      this.data.splice(dropIndex, 0, movedItem);
      this.processDataChanges();
    }

    // Create the reorder event with the calculated order
    const reorderEvent = {
      dragIndex: this.draggedRowIndex,
      dropIndex: dropIndex,
      data: draggedData,
      newOrder: newOrder, // Add the calculated order
    };

    // Emit the reorder event
    this.onRowReorder.emit(reorderEvent);

    // Call the config's onRowReorder handler if it exists
    if (this.config.onRowReorder) {
      this.config.onRowReorder(reorderEvent);
    }

    this.draggedRowIndex = null;
  }

  trackCols(_: number, col: ListingColInterface) {
    return col.id ?? col.label;
  }

  /**
   * Returns visible data (filters out deleted/hidden items)
   */
  get visibleData(): any[] {
    return this._data.filter(item => !item.__class?.["show-deleted"] && !item.__class?.["deleted-row"] && !item.__class?.["to-remove"]);
  }

  /**
   * Handles the call of onCellClick.
   * @param row
   */
  handleCellClick(index: number) {
    if (typeof this.config.onCellClick !== "function") {
      return;
    }
    this.config.onCellClick(this.data[index], index);
  }

  /**
   * primeng has issue to assign right width values when reorder table columns, so override primeng algorithms to update
   * column width styles correctly
   * @private
   */
  private _editTableStyles() {
    // styleElement is just a style tag appended to document.head create with renderer
    // primeng first destroy the styleElement if exist
    this.primeTable()?.destroyStyleElement();
    // then create styleElement again
    this.primeTable()?.createStyleElement();

    // here update every th and td style widths with based on userOptions widths
    let innerHTML = "";
    this.userOptions().order.forEach((colId, index) => {
      const width = this.userOptions().cols?.[colId]?.width;
      let style = `width: ${width}px !important; max-width: ${width}px !important;`;
      innerHTML += `
          #${this.primeTable()?.id}-table > .p-datatable-thead > tr > th:nth-child(${index + 2}),
          #${this.primeTable()?.id}-table > .p-datatable-tbody > tr > td:nth-child(${index + 2}),
          #${this.primeTable()?.id}-table > .p-datatable-tfoot > tr > td:nth-child(${index + 2}){
              ${style}
          }
        `;
    });
    // then update styleElement with renderer again to change the behavior of primeng setting widths
    this.renderer.setProperty(this.primeTable()?.styleElement, "innerHTML", innerHTML);
  }
}
